import React, {
  memo,
  forwardRef,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { Button, Empty, Input, Divider, Radio, Spin, Pagination, Tooltip } from 'antd';
import { capitalize } from 'lodash';

import Modal from 'common/components/modal';
import { IconPropertyOutline } from '~components/svgs';

import { QUERY_LANDLORD_PROPERTIES } from '../queries';
import { propertyState } from '~constants';
import { propertyStatus } from '~constants/listing-management';
import generatePath from '~settings/routing';

import useStyles from './styles';

const SelectPropertyModal = memo(
  forwardRef(({ booking, handleOpenCounterOffer }, ref) => {
    const history = useHistory();

    const [visible, setVisible] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [searchVal, setSearchVal] = useState(null);

    const [selectProperty, setSelectProperty] = useState(null);

    const { t } = useTranslation();
    const classes = useStyles();

    const [queryProperties, { data, loading }] = useLazyQuery(QUERY_LANDLORD_PROPERTIES, {
      fetchPolicy: 'network-only',
      variables: {
        citySlug: booking?.property?.city?.slug,
      },
    });

    const { properties, total } = useMemo(() => {
      const propertyData = data?.properties;

      return {
        properties: propertyData?.edges?.filter(item => item.node.id !== booking?.property?.id),
        total: propertyData?.totalCount,
      };
    }, [data, booking]);

    const handleOpenModal = useCallback(() => {
      setVisible(true);

      queryProperties();
    });

    const handleCloseModal = useCallback(() => {
      setVisible(false);
    });

    const handleSearchProperty = useCallback(
      ev => {
        const name = ev.target.value;

        setSearchVal(name);

        queryProperties({
          variables: {
            pageNumber,
            pageSize,
            name,
            citySlug: booking?.property?.city?.slug,
          },
        });
      },
      [pageNumber, pageSize, booking, queryProperties],
    );

    const handlePropertyChange = useCallback(
      ev => {
        const target = properties?.find(item => item.node.id === ev.target.value);

        setSelectProperty(target?.node);
      },
      [properties],
    );

    const handlePaginationChange = useCallback(
      (page, size) => {
        setPageNumber(page);
        setPageSize(size);

        queryProperties({
          variables: {
            pageNumber: page,
            pageSize: size,
            name: searchVal,
            citySlug: booking?.property?.city?.slug,
          },
        });
      },
      [searchVal, booking],
    );

    const handleOpenNewProperty = useCallback(() => {
      history.push(generatePath('property.create', {}));
    });

    const handleConfirmSelectProperty = useCallback(() => {
      handleCloseModal();
      handleOpenCounterOffer(selectProperty);
    }, [selectProperty]);

    useImperativeHandle(ref, () => ({
      handleOpenModal,
    }));

    return (
      <Modal
        title={t('landlord.long_tail.booking.counter_property_modal.title')}
        width={812}
        visible={visible}
        wrapClassName={classes.wrapper}
        destroyOnClose={true}
        okText={t('landlord.long_tail.booking.counter_modal.footer.confirm')}
        cancelButtonProps={{
          className: classes.cancelButton,
        }}
        okButtonProps={{
          className: classes.okButton,
          disabled: !properties?.length || !selectProperty,
        }}
        onOk={handleConfirmSelectProperty}
        onCancel={handleCloseModal}
      >
        <p className={classes.summary}>
          {t('landlord.long_tail.booking.counter_property_modal.summary')}
        </p>

        <div className={classes.originProperty}>
          <IconPropertyOutline className={classes.propertyIcon} />
          <span className={classes.originPropertySpan}>
            {t('landlord.long_tail.booking.counter_property_modal.original_property')}
          </span>
          <Divider type="vertical" />
          <span className={classes.originPropertyName}>{booking?.property?.name}</span>
        </div>

        <div className={classes.propertyContainer}>
          <div className={classes.propertyLabel}>
            {t('landlord.long_tail.booking.counter_property_modal.label')}
          </div>

          <div className={classes.properties}>
            <div className={classes.selected}>
              {t('landlord.long_tail.booking.counter_property_modal.selected')}:{' '}
              {selectProperty?.name}
            </div>

            <div className={classes.search}>
              <Input.Search
                placeholder={t(
                  'landlord.long_tail.booking.counter_property_modal.search.placeholder',
                )}
                value={searchVal}
                onChange={handleSearchProperty}
              />
            </div>

            <div className={`${classes.result} ${properties?.length === 0 && classes.emptyResult}`}>
              <Choose>
                <When condition={loading}>
                  <Spin />
                </When>
                <When condition={properties?.length > 0}>
                  <Radio.Group
                    style={{ width: '100%' }}
                    value={selectProperty?.id}
                    onChange={handlePropertyChange}
                  >
                    {properties.map(property => {
                      const disabled = [
                        propertyStatus.EDITING,
                        propertyStatus.UNPUBLISHED,
                      ].includes(property?.node?.status);

                      return (
                        <Radio
                          className={`${classes.radio} ${
                            selectProperty?.id === property?.node?.id ? classes.selectedRadio : ''
                          } ${disabled ? classes.disabledRadio : ''}`}
                          value={property?.node?.id}
                          disabled={
                            disabled ||
                            property.state === null ||
                            property?.state === propertyState.SOLD_OUT
                          }
                        >
                          <Tooltip
                            title={
                              disabled
                                ? `${t(
                                    'landlord.long_tail.booking.counter_property_modal.disabled.tooltip',
                                  )} ${capitalize(property?.node?.status)}`
                                : null
                            }
                            placement="topLeft"
                          >
                            {property?.node?.name}
                          </Tooltip>
                        </Radio>
                      );
                    })}
                  </Radio.Group>

                  <Pagination
                    className={classes.pagination}
                    showSizeChanger
                    current={pageNumber}
                    total={total}
                    pageSize={pageSize}
                    onChange={handlePaginationChange}
                    onShowSizeChange={handlePaginationChange}
                  />
                </When>
                <Otherwise>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('landlord.long_tail.booking.counter_property_modal.empty_desc')}
                  />
                </Otherwise>
              </Choose>
            </div>
          </div>
        </div>

        <p className={classes.notice}>
          {t('landlord.long_tail.booking.counter_property_modal.notice')}{' '}
          <Button
            type="link"
            className={classes.createPropertyBtn}
            style={{ paddingLeft: 3, height: 22 }}
            onClick={handleOpenNewProperty}
          >
            {t('landlord.long_tail.booking.counter_property_modal.go_create_property')}
          </Button>
        </p>
      </Modal>
    );
  }),
);

export default SelectPropertyModal;
